import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  downloadMongoExport,
  mongoImportApply,
  mongoImportDryRun,
  type ImportDryRunPreviewRow,
  type ImportDryRunResponse,
} from "../api/adminMongoBackupApi";
import { toast } from "react-toastify";

const MAX_CLIENT_PARSE = 25 * 1024 * 1024;

const ARCHIVED_PREFIX = "_archived_";

function isArchivedMongoCollectionName(name: string): boolean {
  return name.startsWith(ARCHIVED_PREFIX);
}

type CollectionShowFilter = "all" | "not_archived" | "archived";

function filterCollectionsByShow(names: string[], show: CollectionShowFilter): string[] {
  if (show === "all") {
    return names;
  }
  if (show === "archived") {
    return names.filter(isArchivedMongoCollectionName);
  }
  return names.filter((n) => !isArchivedMongoCollectionName(n));
}

type Tab = "export" | "import";

type MongoBackupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Collection names currently on the server (from list API). */
  serverCollections: string[];
};

function toggleSetMember<T>(set: Set<T>, value: T, on: boolean): Set<T> {
  const next = new Set(set);
  if (on) {
    next.add(value);
  } else {
    next.delete(value);
  }
  return next;
}

function visibleSelectionMode(visible: string[], selected: Set<string>): "none" | "some" | "all" {
  if (visible.length === 0) {
    return "none";
  }
  let n = 0;
  for (const v of visible) {
    if (selected.has(v)) {
      n += 1;
    }
  }
  if (n === 0) {
    return "none";
  }
  if (n === visible.length) {
    return "all";
  }
  return "some";
}

function describePreviewRow(row: ImportDryRunPreviewRow): string {
  if (row.mode === "replace") {
    return `Remove ${row.wouldDeleteExisting} existing, insert ${row.wouldInsert} from file`;
  }
  return `Insert ${row.wouldInsert} (incl. ${row.wouldInsertNoMatchKey} without _id/uid match key), update ${row.wouldUpdate}, leave unchanged ${row.wouldUnchanged}`;
}

export function MongoBackupDialog({ open, onOpenChange, serverCollections }: MongoBackupDialogProps) {
  const [tab, setTab] = useState<Tab>("export");
  const sortedServer = useMemo(
    () => [...serverCollections].sort((a, b) => a.localeCompare(b)),
    [serverCollections]
  );

  const [exportShowFilter, setExportShowFilter] = useState<CollectionShowFilter>("all");
  const [exportSelected, setExportSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importShowFilter, setImportShowFilter] = useState<CollectionShowFilter>("all");
  const [importPick, setImportPick] = useState<Set<string>>(new Set());
  const [importPickList, setImportPickList] = useState<string[]>([]);
  const [importMode, setImportMode] = useState<"replace" | "merge">("merge");
  const [dryRun, setDryRun] = useState<ImportDryRunResponse | null>(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTab("export");
    setExportShowFilter("all");
    setExportSelected(new Set(sortedServer));
    setImportFile(null);
    setImportShowFilter("all");
    setImportPick(new Set());
    setImportPickList([]);
    setImportMode("merge");
    setDryRun(null);
  }, [open, sortedServer]);

  const visibleExportCollections = useMemo(
    () => filterCollectionsByShow(sortedServer, exportShowFilter),
    [sortedServer, exportShowFilter]
  );

  const visibleImportCollections = useMemo(
    () => filterCollectionsByShow(importPickList, importShowFilter),
    [importPickList, importShowFilter]
  );

  const exportPickList = useMemo(() => [...exportSelected].sort((a, b) => a.localeCompare(b)), [exportSelected]);

  const exportVisibleMode = useMemo(
    () => visibleSelectionMode(visibleExportCollections, exportSelected),
    [visibleExportCollections, exportSelected]
  );

  const importVisibleMode = useMemo(
    () => visibleSelectionMode(visibleImportCollections, importPick),
    [visibleImportCollections, importPick]
  );

  const handleExport = useCallback(async () => {
    if (exportPickList.length === 0) {
      toast.warn("Select at least one collection to export.");
      return;
    }
    setExporting(true);
    try {
      await downloadMongoExport(exportPickList.length === sortedServer.length ? "all" : exportPickList);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }, [exportPickList, sortedServer.length]);

  const onImportFileChange = useCallback((file: File | null) => {
    setImportFile(file);
    setDryRun(null);
    setImportShowFilter("all");
    if (!file) {
      setImportPick(new Set());
      setImportPickList([]);
      return;
    }
    if (file.size > MAX_CLIENT_PARSE) {
      setImportPickList([]);
      toast.info("Large file: run dry run to see collections in file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const j = JSON.parse(text) as {
          labwickMongoExport?: { collections?: Record<string, unknown> };
          collections?: Record<string, unknown>;
        };
        const root = j.labwickMongoExport && typeof j.labwickMongoExport === "object" ? j.labwickMongoExport : j;
        const cols = root.collections && typeof root.collections === "object" ? Object.keys(root.collections) : [];
        const safe = cols.filter(
          (n) =>
            typeof n === "string" &&
            /^[a-zA-Z0-9_.-]{1,200}$/.test(n) &&
            !n.startsWith("system.")
        );
        safe.sort((a, b) => a.localeCompare(b));
        setImportPickList(safe);
        setImportPick(new Set(safe));
      } catch {
        toast.error("Could not parse JSON. Check the file format.");
        setImportPickList([]);
        setImportPick(new Set());
      }
    };
    reader.readAsText(file);
  }, []);

  const importSelectionParam = useMemo((): string[] | "all" => {
    if (importPickList.length === 0) {
      return "all";
    }
    if (importPick.size === importPickList.length) {
      return "all";
    }
    return [...importPick].sort((a, b) => a.localeCompare(b));
  }, [importPick, importPickList]);

  const handleDryRun = useCallback(async () => {
    if (!importFile) {
      toast.warn("Choose an export JSON file first.");
      return;
    }
    setDryRunning(true);
    setDryRun(null);
    try {
      const res = await mongoImportDryRun(importFile, importMode, importSelectionParam);
      setDryRun(res);
      if (res.collectionsInFile?.length && importPickList.length === 0) {
        const safe = [...res.collectionsInFile].sort((a, b) => a.localeCompare(b));
        setImportPickList(safe);
        setImportPick(new Set(safe));
      }
      toast.success("Dry run complete.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Dry run failed");
    } finally {
      setDryRunning(false);
    }
  }, [importFile, importMode, importSelectionParam, importPickList.length]);

  const handleApply = useCallback(async () => {
    if (!importFile) {
      toast.warn("Choose a file first.");
      return;
    }
    if (!dryRun?.success) {
      toast.warn("Run a successful dry run before applying.");
      return;
    }
    setApplying(true);
    try {
      await mongoImportApply(importFile, importMode, importSelectionParam);
      toast.success("Import completed.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Import failed");
    } finally {
      setApplying(false);
    }
  }, [importFile, importMode, importSelectionParam, dryRun?.success, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(96vw,36rem)] max-h-[min(90dvh,720px)] flex flex-col gap-3 overflow-hidden dark:bg-gray-950">
        <DialogHeader className="shrink-0">
          <DialogTitle>Export / import data</DialogTitle>
          <DialogDescription>
            JSON bundles produced by this tool. Replace clears each selected collection then inserts file rows.
            Merge upserts by <code className="text-xs">_id</code> or <code className="text-xs">uid</code>;
            documents without both always insert as new. Merge dry run shows inserts, updates, and unchanged rows.
          </DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 gap-1 rounded-md border p-0.5 w-fit">
          <Button type="button" size="sm" variant={tab === "export" ? "default" : "ghost"} onClick={() => setTab("export")}>
            Export
          </Button>
          <Button type="button" size="sm" variant={tab === "import" ? "default" : "ghost"} onClick={() => setTab("import")}>
            Import
          </Button>
        </div>

        {tab === "export" ? (
          <div className="min-h-0 flex-1 flex flex-col gap-3 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">Show</span>
              <div className="flex rounded-md border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900">
                <Button
                  type="button"
                  size="sm"
                  variant={exportShowFilter === "all" ? "default" : "ghost"}
                  className="h-8 px-2.5"
                  onClick={() => setExportShowFilter("all")}
                >
                  All
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={exportShowFilter === "not_archived" ? "default" : "ghost"}
                  className="h-8 px-2.5"
                  onClick={() => setExportShowFilter("not_archived")}
                >
                  Not archived
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={exportShowFilter === "archived" ? "default" : "ghost"}
                  className="h-8 px-2.5"
                  onClick={() => setExportShowFilter("archived")}
                >
                  Archived only
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Checkbox
                id="mongo-backup-export-select-visible"
                disabled={visibleExportCollections.length === 0}
                checked={
                  exportVisibleMode === "all"
                    ? true
                    : exportVisibleMode === "some"
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={() => {
                  if (exportVisibleMode === "all") {
                    setExportSelected((prev) => {
                      const next = new Set(prev);
                      for (const n of visibleExportCollections) {
                        next.delete(n);
                      }
                      return next;
                    });
                  } else {
                    setExportSelected((prev) => {
                      const next = new Set(prev);
                      for (const n of visibleExportCollections) {
                        next.add(n);
                      }
                      return next;
                    });
                  }
                }}
              />
              <Label htmlFor="mongo-backup-export-select-visible" className="text-sm font-medium cursor-pointer">
                Collections on server (
                {exportShowFilter === "all" ? sortedServer.length : `${visibleExportCollections.length} of ${sortedServer.length}`}
                )
              </Label>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 p-2 space-y-2">
              {sortedServer.length === 0 ? (
                <p className="text-sm text-muted-foreground">No collections loaded. Refresh from the main page.</p>
              ) : visibleExportCollections.length === 0 ? (
                <p className="text-sm text-muted-foreground">No collections match this filter.</p>
              ) : (
                visibleExportCollections.map((name) => (
                  <label key={name} className="flex items-center gap-2 text-sm cursor-pointer font-mono">
                    <Checkbox
                      checked={exportSelected.has(name)}
                      onCheckedChange={(v) =>
                        setExportSelected((prev) => toggleSetMember(prev, name, v === true))
                      }
                    />
                    {name}
                  </label>
                ))
              )}
            </div>
            <div className="shrink-0 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button type="button" disabled={exporting || exportPickList.length === 0} onClick={() => void handleExport()}>
                {exporting ? "Downloading…" : "Download JSON"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 flex flex-col gap-3 overflow-y-auto">
            <div className="space-y-2 shrink-0">
              <Label htmlFor="mongo-backup-file">Export JSON file</Label>
              <input
                id="mongo-backup-file"
                type="file"
                accept="application/json,.json"
                className="block w-full text-sm text-muted-foreground file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border file:text-sm"
                onChange={(e) => onImportFileChange(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="flex flex-wrap gap-3 shrink-0">
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Mode</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={importMode === "merge" ? "default" : "outline"}
                    onClick={() => {
                      setImportMode("merge");
                      setDryRun(null);
                    }}
                  >
                    Merge (upsert)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={importMode === "replace" ? "default" : "outline"}
                    onClick={() => {
                      setImportMode("replace");
                      setDryRun(null);
                    }}
                  >
                    Replace (clear + insert)
                  </Button>
                </div>
              </div>
            </div>

            {importPickList.length > 0 ? (
              <div className="space-y-2 shrink-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">Show</span>
                  <div className="flex rounded-md border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900">
                    <Button
                      type="button"
                      size="sm"
                      variant={importShowFilter === "all" ? "default" : "ghost"}
                      className="h-8 px-2.5"
                      onClick={() => setImportShowFilter("all")}
                    >
                      All
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={importShowFilter === "not_archived" ? "default" : "ghost"}
                      className="h-8 px-2.5"
                      onClick={() => setImportShowFilter("not_archived")}
                    >
                      Not archived
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={importShowFilter === "archived" ? "default" : "ghost"}
                      className="h-8 px-2.5"
                      onClick={() => setImportShowFilter("archived")}
                    >
                      Archived only
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="mongo-backup-import-select-visible"
                    disabled={visibleImportCollections.length === 0}
                    checked={
                      importVisibleMode === "all"
                        ? true
                        : importVisibleMode === "some"
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={() => {
                      if (importVisibleMode === "all") {
                        setImportPick((prev) => {
                          const next = new Set(prev);
                          for (const n of visibleImportCollections) {
                            next.delete(n);
                          }
                          return next;
                        });
                      } else {
                        setImportPick((prev) => {
                          const next = new Set(prev);
                          for (const n of visibleImportCollections) {
                            next.add(n);
                          }
                          return next;
                        });
                      }
                    }}
                  />
                  <Label htmlFor="mongo-backup-import-select-visible" className="text-sm cursor-pointer">
                    Collections in file (
                    {importShowFilter === "all"
                      ? importPickList.length
                      : `${visibleImportCollections.length} of ${importPickList.length}`}
                    )
                  </Label>
                </div>
                <div className="max-h-[140px] overflow-y-auto rounded-md border p-2 space-y-2">
                  {visibleImportCollections.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No collections match this filter.</p>
                  ) : (
                    visibleImportCollections.map((name) => (
                      <label key={name} className="flex items-center gap-2 text-sm cursor-pointer font-mono">
                        <Checkbox
                          checked={importPick.has(name)}
                          onCheckedChange={(v) =>
                            setImportPick((prev) => toggleSetMember(prev, name, v === true))
                          }
                        />
                        {name}
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : importFile ? (
              <p className="text-xs text-muted-foreground shrink-0">
                Collection list unavailable locally. Dry run will show collections and preview.
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2 shrink-0">
              <Button type="button" variant="secondary" disabled={!importFile || dryRunning} onClick={() => void handleDryRun()}>
                {dryRunning ? "Running…" : "Dry run"}
              </Button>
              <Button
                type="button"
                disabled={!importFile || !dryRun?.success || applying}
                onClick={() => void handleApply()}
              >
                {applying ? "Applying…" : "Apply import"}
              </Button>
            </div>

            {dryRun?.success && dryRun.preview && dryRun.preview.length > 0 ? (
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2 shrink-0">
                <div className="font-medium">Dry run ({dryRun.mode})</div>
                {dryRun.fileDatabase != null ? (
                  <div className="text-xs text-muted-foreground">File database: {dryRun.fileDatabase || "—"}</div>
                ) : null}
                <ul className="space-y-2 list-none pl-0">
                  {dryRun.preview.map((row) => (
                    <li key={row.collection} className="border-b border-border/60 pb-2 last:border-0">
                      <span className="font-mono text-xs">{row.collection}</span>
                      <div className="text-xs text-muted-foreground mt-1">{describePreviewRow(row)}</div>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 dark:text-amber-300/90">
                  Applying will perform these changes. Replace mode deletes all documents in each selected collection
                  first.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
